/** Hosted Sites page — upload, browse, preview & share static HTML/ZIP sites */

import { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Upload, Search, Globe, Trash2, Copy, ExternalLink, FileCode, ArrowLeft, Check, Package } from 'lucide-react'
import { cn } from '../lib/utils'
import { SITES_BASE } from '../lib/api-client'
import { useAppStore } from '../stores/app-store'
import { useHostedSites, useUploadSite, useDeleteSite } from '../hooks/use-hosted-sites'
import { ConfirmDialog } from '../components/ui/confirm-dialog'
import type { HostedSite } from '../hooks/use-hosted-sites'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Full public URL for a hosted site (served from separate sites domain for credential isolation, GH-121) */
function sitePublicUrl(site: HostedSite): string {
  if (SITES_BASE) return `${SITES_BASE}${site.url}`
  // Derive sites origin from current origin: app.X → sites.X, localhost → api.X (dev fallback)
  const origin = window.location.origin
  if (origin.includes('app.')) return `${origin.replace('app.', 'sites.')}${site.url}`
  // Dev: sites served from same origin via Vite proxy
  return `${origin}${site.url}`
}

export function HostedSitesPage() {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [search, setSearch] = useState('')
  const [description, setDescription] = useState('')
  const [autoSummary, setAutoSummary] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [previewSite, setPreviewSite] = useState<HostedSite | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<HostedSite | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const { data, isLoading } = useHostedSites(search || undefined)
  const uploadSite = useUploadSite()
  const deleteSite = useDeleteSite()

  const sites = data?.sites ?? []
  const total = data?.total ?? 0

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await uploadSite.mutateAsync({ file, description: description || undefined, autoSummary: autoSummary || undefined })
      setDescription('')
      setAutoSummary(false)
      setShowUpload(false)
    } catch {
      // error handled by mutation state
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [uploadSite, description, autoSummary])

  const handleCopyLink = useCallback((site: HostedSite) => {
    void navigator.clipboard.writeText(sitePublicUrl(site))
    setCopiedId(site.id)
    setTimeout(() => setCopiedId(null), 2000)
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    await deleteSite.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, deleteSite])

  return (
    <div className={cn('flex h-screen flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Top bar */}
      <div className={cn('flex items-center gap-3 border-b px-4 py-3', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}>
        <button
          onClick={() => navigate(-1)}
          className={cn('rounded-md p-1', isDark ? 'text-neutral-500 hover:bg-surface-3 hover:text-neutral-300' : 'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700')}
          title="Go back"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <Globe className={cn('h-4 w-4', isDark ? 'text-brand-400' : 'text-brand-600')} />
        <h1 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
          Hosted Sites
        </h1>
        <span className={cn('text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
          {total} {total === 1 ? 'site' : 'sites'}
        </span>
        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <Search className={cn('absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2', isDark ? 'text-neutral-500' : 'text-neutral-400')} />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'w-28 rounded-lg border py-1.5 pl-8 pr-3 text-xs outline-none sm:w-44',
              isDark
                ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500 focus:border-brand-500/50'
                : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400 focus:border-brand-500',
            )}
          />
        </div>

        {/* Upload button — toggles the inline upload panel below */}
        <button
          onClick={() => setShowUpload((v) => !v)}
          aria-expanded={showUpload}
          aria-controls="hosted-sites-upload-panel"
          className="flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
        >
          <Upload className="h-3.5 w-3.5" aria-hidden="true" />
          Upload Site
        </button>
      </div>

      {/* Upload panel (collapsible) */}
      {showUpload && (
        <section
          id="hosted-sites-upload-panel"
          aria-label="Upload a new hosted site"
          className={cn('border-b px-4 py-3', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}
        >
          <div className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <label
                htmlFor="hosted-sites-description"
                className={cn('block text-xs font-medium', isDark ? 'text-neutral-300' : 'text-neutral-600')}
              >
                Description (optional)
              </label>
              <input
                id="hosted-sites-description"
                autoFocus
                type="text"
                placeholder="e.g. Q1 Revenue Dashboard"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={cn(
                  'w-full rounded-lg border px-3 py-1.5 text-xs outline-none',
                  isDark
                    ? 'border-white/[0.06] bg-surface-2 text-neutral-200 placeholder-neutral-500'
                    : 'border-neutral-200 bg-neutral-50 text-neutral-900 placeholder-neutral-400',
                )}
              />
            </div>
            <label className={cn('flex items-center gap-1.5 text-xs', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
              <input type="checkbox" checked={autoSummary} onChange={(e) => setAutoSummary(e.target.checked)} className="rounded" />
              AI Summary
            </label>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadSite.isPending}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
                uploadSite.isPending
                  ? 'cursor-wait bg-brand-600/50 text-white/50'
                  : 'bg-brand-600 text-white hover:bg-brand-500',
              )}
            >
              <FileCode className="h-3.5 w-3.5" />
              {uploadSite.isPending ? 'Uploading...' : 'Choose .html or .zip'}
            </button>
            <button
              onClick={() => setShowUpload(false)}
              className={cn('rounded-lg px-3 py-1.5 text-xs', isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700')}
            >
              Cancel
            </button>
          </div>
          {uploadSite.isError && (
            <p className="mt-2 text-center text-xs text-red-500">{(uploadSite.error as Error).message}</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".html,.htm,.zip"
            onChange={handleUpload}
            className="hidden"
          />
        </section>
      )}

      {/* Sites list */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-400 border-t-brand-500" />
          </div>
        ) : sites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Globe className={cn('mb-3 h-10 w-10', isDark ? 'text-neutral-600' : 'text-neutral-300')} />
            <p className={cn('text-sm', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
              {search ? 'No sites match your search' : 'No hosted sites yet'}
            </p>
            <p className={cn('mt-1 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              Upload an HTML file or ZIP archive to get a shareable link
            </p>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-2">
            {sites.map((site) => (
              <div
                key={site.id}
                className={cn(
                  'group rounded-lg border p-3 transition-colors',
                  isDark
                    ? 'border-white/[0.06] bg-surface-1 hover:bg-surface-2'
                    : 'border-neutral-200 bg-white hover:bg-neutral-50',
                )}
              >
                {/* Top row: icon + info + desktop actions */}
                <div className="flex items-center gap-3">
                  <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', isDark ? 'bg-surface-3' : 'bg-neutral-100')}>
                    <FileCode className={cn('h-4 w-4', site.status === 'ready' ? (isDark ? 'text-brand-400' : 'text-brand-600') : 'text-neutral-400')} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={cn('truncate text-sm font-medium', isDark ? 'text-neutral-200' : 'text-neutral-800')}>
                        {site.filename}
                      </p>
                      <span className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                        site.status === 'ready'
                          ? 'bg-green-500/10 text-green-500'
                          : site.status === 'failed'
                            ? 'bg-red-500/10 text-red-500'
                            : 'bg-yellow-500/10 text-yellow-500',
                      )}>
                        {site.status}
                      </span>
                    </div>
                    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
                      <span>{formatBytes(site.totalSizeBytes)}</span>
                      <span className="hidden sm:inline">·</span>
                      <span>{site.fileCount} {site.fileCount === 1 ? 'file' : 'files'}</span>
                      <span className="hidden sm:inline">·</span>
                      <span>{formatDate(site.createdAt)}</span>
                      {site.description && (
                        <>
                          <span className="hidden sm:inline">·</span>
                          <span className="hidden truncate sm:inline">{site.description}</span>
                        </>
                      )}
                    </div>
                    {/* GH-259: source package chip — present when site is linked to an onboarding package */}
                    {site.sourcePackage ? (
                      <div className={cn('mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium', isDark ? 'bg-brand-500/10 text-brand-400' : 'bg-brand-50 text-brand-700')}>
                        <Package className="h-2.5 w-2.5" aria-hidden="true" />
                        {site.sourcePackage.name}
                      </div>
                    ) : null}
                  </div>
                  {/* Desktop actions — hover-reveal */}
                  <div className="hidden shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
                    <button
                      onClick={() => handleCopyLink(site)}
                      className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
                      title="Copy link"
                      aria-label={`Copy link to ${site.filename}`}
                    >
                      {copiedId === site.id
                        ? <Check className="h-3.5 w-3.5 text-green-500" aria-hidden="true" />
                        : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
                    </button>
                    <a
                      href={sitePublicUrl(site)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
                      title="Open in new tab"
                      aria-label={`Open ${site.filename} in new tab`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                    <button
                      onClick={() => setPreviewSite(previewSite?.id === site.id ? null : site)}
                      className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-neutral-400 hover:text-neutral-200' : 'hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700')}
                      title="Preview"
                      aria-label={`Preview ${site.filename}`}
                      aria-pressed={previewSite?.id === site.id}
                    >
                      <Globe className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(site)}
                      className={cn('rounded-md p-1.5', isDark ? 'hover:bg-surface-3 text-red-400/70 hover:text-red-400' : 'hover:bg-neutral-100 text-red-400/70 hover:text-red-500')}
                      title="Delete"
                      aria-label={`Delete ${site.filename}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
                {/* Mobile actions — always visible, full-width row */}
                <div className={cn('mt-2 flex items-center gap-1 border-t pt-2 sm:hidden', isDark ? 'border-white/[0.06]' : 'border-neutral-100')}>
                  <button
                    onClick={() => handleCopyLink(site)}
                    className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
                  >
                    {copiedId === site.id ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                    Copy
                  </button>
                  <a
                    href={sitePublicUrl(site)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open
                  </a>
                  <button
                    onClick={() => setPreviewSite(previewSite?.id === site.id ? null : site)}
                    className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs', isDark ? 'text-neutral-400 active:bg-surface-3' : 'text-neutral-500 active:bg-neutral-100')}
                  >
                    <Globe className="h-3.5 w-3.5" />
                    Preview
                  </button>
                  <button
                    onClick={() => setDeleteTarget(site)}
                    className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md p-2 text-xs text-red-400', isDark ? 'active:bg-surface-3' : 'active:bg-neutral-100')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview panel (bottom) */}
      {previewSite && (
        <div className={cn('border-t', isDark ? 'border-white/[0.06]' : 'border-neutral-200')} style={{ height: '50vh' }}>
          <div className={cn('flex items-center gap-2 px-3 py-1.5', isDark ? 'bg-surface-1' : 'bg-white')}>
            <Globe className="h-3 w-3 text-brand-500" />
            <span className={cn('flex-1 truncate text-xs', isDark ? 'text-neutral-300' : 'text-neutral-600')}>
              {previewSite.filename}
            </span>
            <a
              href={sitePublicUrl(previewSite)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-500 hover:underline"
            >
              Open
            </a>
            <button
              onClick={() => setPreviewSite(null)}
              className={cn('text-xs', isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700')}
            >
              Close
            </button>
          </div>
          <iframe
            src={sitePublicUrl(previewSite)}
            title={previewSite.filename}
            className="h-full w-full border-0"
            sandbox="allow-scripts"
          />
        </div>
      )}

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete site"
        description={`Delete "${deleteTarget?.filename}" and all its files? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  )
}
